import React, { useEffect, useState, useMemo } from 'react'
import { PairingCategory, FontPairing } from '../../font-explorer/pairings-data'
import { PAIRING_CATEGORIES } from '../../font-explorer/pairings-data'
import { pairingsApi } from '../../font-explorer/api/pairingsApi'
import { buildGoogleFontsUrl } from '../../font-explorer/fonts'
import Header from './Header'
import PairingCard from '../../font-explorer/components/PairingCard'

const DEFAULT_SAMPLE_TEXT = 'The quick brown fox jumps over the lazy dog'
const PAIRINGS_PER_PAGE = 20

interface FlatPairing extends FontPairing {
  categoryName: string
  categoryEmoji: string
  categoryId: string
}

interface AllPairingsProps {
  refreshKey?: number
  isPairingFavorited?: (numericId: number) => boolean
  isPairingSortFavorited?: (numericId: number) => boolean
  togglePairing?: (numericId: number) => void
  authenticated?: boolean
  pairingSortSnapshot?: Set<number>
}

export default function AllPairings({ refreshKey, isPairingFavorited, isPairingSortFavorited, togglePairing, authenticated, pairingSortSnapshot }: AllPairingsProps) {
  const [categories, setCategories] = useState<PairingCategory[]>(PAIRING_CATEGORIES)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [displayFontSize, setDisplayFontSize] = useState(36)
  const [bodyFontSize, setBodyFontSize] = useState(18)
  const [sampleText, setSampleText] = useState(DEFAULT_SAMPLE_TEXT)
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set())
  const [visibleCount, setVisibleCount] = useState(PAIRINGS_PER_PAGE)

  useEffect(() => {
    pairingsApi
      .listCategories()
      .then((apiCategories) => {
        if (apiCategories.length > 0) {
          setCategories(apiCategories)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [refreshKey])

  // Flatten all pairings with category metadata
  const allPairings: FlatPairing[] = useMemo(() => {
    return categories.flatMap((cat) =>
      cat.pairings.map((p) => ({
        ...p,
        categoryName: cat.name,
        categoryEmoji: cat.emoji,
        categoryId: cat.id,
      }))
    )
  }, [categories])

  // Filter by search + category, then sort favorites first
  const filteredPairings = useMemo(() => {
    let pairings = allPairings

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      pairings = pairings.filter(
        (p) =>
          p.displayFont.toLowerCase().includes(q) ||
          p.bodyFont.toLowerCase().includes(q) ||
          (p.name && p.name.toLowerCase().includes(q))
      )
    }

    if (categoryFilter) {
      pairings = pairings.filter((p) => p.categoryId === categoryFilter)
    }

    // Sort favorites first (uses frozen snapshot so toggling doesn't reshuffle)
    if (isPairingSortFavorited) {
      pairings = [...pairings].sort((a, b) => {
        const aFav = a.numericId ? isPairingSortFavorited(a.numericId) : false
        const bFav = b.numericId ? isPairingSortFavorited(b.numericId) : false
        if (aFav && !bFav) return -1
        if (!aFav && bFav) return 1
        return 0
      })
    }

    return pairings
  }, [allPairings, searchQuery, categoryFilter, isPairingSortFavorited, pairingSortSnapshot])

  // Visible pairings (lazy load)
  const visiblePairings = useMemo(() => {
    return filteredPairings.slice(0, visibleCount)
  }, [filteredPairings, visibleCount])

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(PAIRINGS_PER_PAGE)
  }, [searchQuery, categoryFilter])

  // Load fonts for visible pairings
  useEffect(() => {
    const fontsToLoad = visiblePairings.flatMap((p) => [p.displayFont, p.bodyFont])
    const uniqueFonts = Array.from(new Set(fontsToLoad))
    const newFonts = uniqueFonts.filter((f) => !loadedFonts.has(f))

    if (newFonts.length > 0) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = buildGoogleFontsUrl(newFonts)
      document.head.appendChild(link)

      setLoadedFonts((prev) => {
        const next = new Set(prev)
        newFonts.forEach((f) => next.add(f))
        return next
      })
    }
  }, [visiblePairings, loadedFonts])

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + PAIRINGS_PER_PAGE, filteredPairings.length))
  }

  const totalPairings = allPairings.length
  const totalCategories = categories.length

  return (
    <div className="min-h-screen bg-gray-50">
      <Header subtitle={`${totalPairings} pairings across ${totalCategories} categories`} />

      {/* Controls */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by font name or pairing name..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.emoji} {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Display Font Size */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Display: {displayFontSize}px
              </label>
              <input
                type="range"
                min="24"
                max="72"
                value={displayFontSize}
                onChange={(e) => setDisplayFontSize(Number(e.target.value))}
                className="w-32 cursor-pointer"
              />
            </div>

            {/* Body Font Size */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Body: {bodyFontSize}px
              </label>
              <input
                type="range"
                min="12"
                max="32"
                value={bodyFontSize}
                onChange={(e) => setBodyFontSize(Number(e.target.value))}
                className="w-32 cursor-pointer"
              />
            </div>
          </div>

          {/* Sample Text */}
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sample Text</label>
            <input
              type="text"
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              placeholder="Type to preview..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Pairings Grid */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="grid gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 px-4 py-3 animate-pulse">
                <div className="h-8 bg-gray-100 rounded mb-1" />
                <div className="h-5 bg-gray-50 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-48" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-500">
              Showing {Math.min(visibleCount, filteredPairings.length)} of {filteredPairings.length} pairings
            </div>

            <div className="grid gap-3">
              {visiblePairings.map((pairing) => (
                <PairingCard
                  key={pairing.id}
                  pairing={pairing}
                  sampleText={sampleText || DEFAULT_SAMPLE_TEXT}
                  displayFontSize={displayFontSize}
                  bodyFontSize={bodyFontSize}
                  isLoaded={
                    loadedFonts.has(pairing.displayFont) && loadedFonts.has(pairing.bodyFont)
                  }
                  categoryLabel={`${pairing.categoryEmoji} ${pairing.categoryName}`}
                  isFavorited={pairing.numericId ? isPairingFavorited?.(pairing.numericId) : false}
                  onToggleFavorite={pairing.numericId && togglePairing ? () => togglePairing(pairing.numericId!) : undefined}
                  authenticated={authenticated}
                />
              ))}
            </div>

            {visibleCount < filteredPairings.length && (
              <div className="mt-8 text-center">
                <button
                  onClick={loadMore}
                  className="px-6 py-3 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  Load More ({(filteredPairings.length - visibleCount).toLocaleString()} remaining)
                </button>
              </div>
            )}

            {filteredPairings.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No pairings found matching your criteria.</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <p className="text-sm text-gray-500 text-center">
            Powered by{' '}
            <a
              href="https://fonts.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 underline"
            >
              Google Fonts
            </a>
            {' '}&middot; Built by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}
